import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { useTemplateTypeMap } from '@frontend/integrated-messaging';
import { useAppScopeStore } from '@frontend/scope';

/**
 *
 * @param locationIds (optional) The locationIds to filter by. If not provided, the selectedLocationIds from the app scope store will be used
 * @returns A query that fetches the template types for the given locationIds, filtered based on the vertical of the
 * locations, and excluding any unimplemented template types. A template type is considered unimplemented if it is not
 * present in the useTemplateTypeMap.
 */
export const useAvailableTemplateTypesQuery = (locationIds?: string[]) => {
  const { selectedLocationIds, accessibleLocationData, selectedOrgId } = useAppScopeStore();
  const resolvedLocationIds = locationIds ?? selectedLocationIds;
  const locations = resolvedLocationIds.map((id) => accessibleLocationData[id]);
  const allLocationsAreVet = locations.every((location) => location?.vertical === Vertical.VET);
  const templateTypeMap = useTemplateTypeMap();
  const implementedTemplateTypes = Object.keys(templateTypeMap) as TemplateType_Slug[];
  const isReviewsCustomizationOff = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: resolvedLocationIds,
    customizationFlag: Feature.REVIEWS_HOME_ICON,
    activeStates: [CustomizationFlagTypes.CustomizationFlagState.ACTIVE],
  }).isFeatureHiddenInAllLocations;
  const { aggregateValue: reviewsACLAccess } = checkAggregateACL(
    selectedLocationIds,
    [Permission.REVIEW_REPLY_WRITE, Permission.REVIEW_READ],
    'any'
  );
  const isReviewsAccessDenied = isReviewsCustomizationOff || !reviewsACLAccess;
  const templateTypesQuery = TemplatorV2Queries.useTemplateTypesList(
    {
      templateTypeSlugs: implementedTemplateTypes,
      businessGroupIds: resolvedLocationIds,
      orgId: selectedOrgId,
    },
    {
      select: (data) => {
        return data.templateTypes.filter(({ slug }) => {
          if (slug === TemplateType_Slug.MANUAL_MESSAGE && allLocationsAreVet) return false;
          if (slug === TemplateType_Slug.MANUAL_MESSAGE_PET && !allLocationsAreVet) return false;
          if (slug === TemplateType_Slug.MANUAL_REVIEW_REQUEST && (allLocationsAreVet || isReviewsAccessDenied))
            return false;
          if (slug === TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET && (!allLocationsAreVet || isReviewsAccessDenied))
            return false;
          if (slug === TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER && allLocationsAreVet) return false;
          if (slug === TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER_PET && !allLocationsAreVet) return false;
          return true;
        });
      },
    }
  );

  return templateTypesQuery;
};
